import { format, isValid, parseISO } from 'date-fns';
import { numeralFormatterCurrency } from 'ns_libs/formatter';
import { IExtractedPeriod } from 'types/db/ExtractedProForma';
import { IFormattedCashFlowPeriod } from 'views/ProFormaTable/types';
import { PeriodValues } from './types';

export const toNumber = (value: string): number | null => {
    if (value.trim() === '') return null;
    // Extraction preserves the workbook text, so amounts can carry thousands separators ("42,000,000").
    const numeric = Number(value.replace(/,/g, ''));
    return Number.isNaN(numeric) ? null : numeric;
};

export const formatAmount = (value: number | null): string => numeralFormatterCurrency(value ?? 0, '$0,0');

export const formatPeriodHeader = (period: string): string => (
    period.trim() !== '' && !Number.isNaN(Number(period)) ? `Month ${period}` : period
);

// The extractor keeps the date text as printed, so it may not be ISO ("1/1/2025"); fall back to the native parser.
const parsePeriodDate = (date: string): Date | null => {
    const parsed = parseISO(date);
    if (isValid(parsed)) return parsed;
    const fallback = new Date(date);
    return isValid(fallback) ? fallback : null;
};

const formatPeriodDate = (date: string): string => {
    const parsed = parsePeriodDate(date);
    return parsed ? format(parsed, 'MMM yyyy') : date;
};

// Sort by date when both columns have a valid one, else by the period header as a number,
// else keep the first-seen order of the workbook.
const comparePeriods = (left: IExtractedPeriod, right: IExtractedPeriod): number => {
    const leftDate = parsePeriodDate(left.date);
    const rightDate = parsePeriodDate(right.date);
    if (leftDate && rightDate) return leftDate.getTime() - rightDate.getTime();
    const [leftNumber, rightNumber] = [Number(left.period), Number(right.period)];
    if (!Number.isNaN(leftNumber) && !Number.isNaN(rightNumber)) return leftNumber - rightNumber;
    return 0;
};

export const buildPeriodColumns = (rows: IExtractedPeriod[][]): IFormattedCashFlowPeriod[] => {
    const firstByPeriod = new Map<string, IExtractedPeriod>();
    rows.flat().forEach((period) => {
        const existing = firstByPeriod.get(period.period);
        if (!existing || (!existing.date && period.date)) firstByPeriod.set(period.period, period);
    });

    return [...firstByPeriod.values()]
        .sort(comparePeriods)
        .map(period => ({
            period: period.period,
            label: period.date ? formatPeriodDate(period.date) : '',
            value: period.period,
        }));
};

export const periodValues = (periods: IExtractedPeriod[]): PeriodValues => new Map(
    periods.flatMap(({ period, value }) => {
        const numeric = toNumber(value);
        return numeric === null ? [] : [[period, numeric] as const];
    }),
);

export const sumValues = (values: (number | null)[]): number | null => {
    const present = values.filter((value): value is number => value !== null);
    return present.length > 0 ? present.reduce((total, value) => total + value, 0) : null;
};

export const sumPeriodValues = (rows: PeriodValues[]): PeriodValues => {
    const totals: PeriodValues = new Map();
    rows.forEach(row => row.forEach((value, period) => totals.set(period, (totals.get(period) ?? 0) + value)));
    return totals;
};
